import type React from 'react';
import { PureComponent, type ReactNode } from 'react';

import classNames from 'classnames';
import { pick } from 'lodash-es';

import type { AriaProps } from '@iconik-io/shared-lib/src/react/a11y/aria';

import type { IconType } from '../../../icons/constants';
import Button from '../../../ui/button/button';
import type { ButtonSize, ButtonVariant } from '../../../ui/button/types';
import { ui } from '../../../utils/helpers';

import styles from './input_number.module.css';

const HTML_INPUT_PROPERTIES = [
  'id',
  'disabled',
  'readOnly',
  'name',
  'value',
  'min',
  'max',
  'step',
  'placeholder',
  'autoComplete',
  'autoFocus',
];

type Props = {
  id?: string;
  preventValuePropTriggerChange?: boolean;
  minusIcon?: IconType;
  plusIcon?: IconType;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  value?: number;
  min: number;
  max: number;
  step?: number;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  className?: string;
  unit?: string;
  inputClassName?: string;
  measureClassName?: string;
  adjustButtonClassName?: string;
  type?: string;
  onChange?: (value: number | undefined | null) => void;
  onBlur?: (value: number | null) => void;
  buttonVariant?: ButtonVariant;
  buttonSize?: ButtonSize;
  buttonStepValue?: number;
  minusButtonAriaProps?: AriaProps;
  plusButtonAriaProps?: AriaProps;
};

type State = {
  value?: number | null;
};

class InputNumber extends PureComponent<Props, State> {
  static defaultProps = {
    min: 0,
    max: 2999,
  };

  state: State = { value: this.props.value || null };

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.props.value !== prevProps.value) {
      if (this.props.preventValuePropTriggerChange) {
        this.setState({ value: this.props.value });
      } else {
        this.setValue(this.props.value);
      }
    }
  }

  setValue(value: number | undefined | null): void {
    this.setState({ value });
    this.props.onChange?.(value);
  }

  onClickMinus = (): void => {
    const stateValue = this.state.value ?? 0;
    const increase = this.props.buttonStepValue || 1;
    const value = stateValue > 0 ? stateValue - increase : 0;
    if (value < this.props.min) {
      return;
    }
    this.setValue(value);
  };

  onClickPlus = (): void => {
    const increase = this.props.buttonStepValue || 1;
    const value = (this.state.value || 0) + increase;
    if (value > this.props.max) {
      return;
    }
    this.setValue(value);
  };

  onBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    let value: number | null = Number(event.currentTarget.value);
    if (value < this.props.min) {
      value = this.props.min;
    }
    if (value > this.props.max) {
      value = this.props.max;
    }
    if (Number.isNaN(value)) {
      value = null;
    }

    if (value !== this.state.value) {
      this.setValue(value);
    }
    this.props.onBlur?.(value);
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    let value: number | null = Number(event.currentTarget.value);
    if (Number.isNaN(value)) {
      value = null;
    }
    this.props.onChange?.(value);
    this.setState({ value: Number(event.currentTarget.value) });
  };

  render(): ReactNode {
    const inputProps = pick(this.props, HTML_INPUT_PROPERTIES);
    return (
      <div className={classNames(styles.root, this.props.className)}>
        <Button
          className={classNames(
            styles.adjust_button,
            this.props.adjustButtonClassName,
            {
              [styles.adjust_text_button]: !this.props.minusIcon,
            },
          )}
          noBorder
          size={this.props.buttonSize}
          variant={this.props.buttonVariant}
          wideBasis={false}
          onClick={this.onClickMinus}
          icon={this.props.minusIcon}
          disabled={this.props.readOnly || this.props.disabled}
          uiKey="minus"
          aria={{ label: 'Decrease', ...this.props.minusButtonAriaProps }}
        >
          {this.props.minusIcon ? null : '-'}
        </Button>

        <div
          className={classNames({
            [styles.number_unit_input_wrapper]: Boolean(this.props.unit),
          })}
        >
          <input
            {...inputProps}
            inputMode="numeric"
            value={this.state.value || ''}
            type="number"
            lang={window.navigator.language}
            onBlur={this.onBlur}
            onChange={this.onChange}
            className={classNames(styles.input, this.props.inputClassName)}
            {...ui('input-number')}
          />

          {this.props.unit && (
            <span
              className={classNames(styles.unit, this.props.measureClassName)}
            >
              {this.props.unit}
            </span>
          )}
        </div>

        <Button
          className={classNames(
            styles.adjust_button,
            this.props.adjustButtonClassName,
            {
              [styles.adjust_text_button]: !this.props.plusIcon,
            },
          )}
          noBorder
          size={this.props.buttonSize}
          variant={this.props.buttonVariant}
          icon={this.props.plusIcon}
          wideBasis={false}
          onClick={this.onClickPlus}
          disabled={this.props.readOnly || this.props.disabled}
          uiKey="plus"
          aria={{ label: 'Increase', ...this.props.plusButtonAriaProps }}
        >
          {this.props.plusIcon ? null : '+'}
        </Button>
      </div>
    );
  }
}

export default InputNumber;
